import React, { createContext, useContext, useEffect, useState } from "react";
import { createPortal } from "react-dom";

import { useMobileNav } from "../context/MobileNavContext.js";

// Creating the context
const ModalContext = createContext({
  activeModal: "",
  openModal: () => {},
  closeModal: () => {},
});

// Parent component
export function ModalProvider({ children }) {
  const { closeMenu } = useMobileNav();
  const [activeModal, setActiveModal] = useState(null);

  const openModal = (name) => {
    closeMenu();
    setActiveModal(name);
  };

  const closeModal = () => setActiveModal(null);

  return (
    <ModalContext.Provider value={{ activeModal, openModal, closeModal }}>
      {children}
    </ModalContext.Provider>
  );
}

// Child components
function Trigger({ children, name }) {
  const { openModal } = useContext(ModalContext);
  return <div onClick={() => openModal(name)}>{children}</div>;
}

function Content({ children, name, title = "Video Preview" }) {
  const { activeModal, closeModal } = useContext(ModalContext);

  // ESC per chiudere + lock dello scroll
  useEffect(() => {
    if (activeModal !== name) return;

    const handleKeyPress = (event) => {
      if (event.key === "Escape") closeModal();
    };
    document.addEventListener("keydown", handleKeyPress);

    // lock scroll
    const prevOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyPress);
      document.documentElement.style.overflow = prevOverflow || "";
    };
  }, [activeModal, name, closeModal]);

  if (activeModal !== name) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[100] bg-black/90 animate-fadeInForwards"
      onClick={closeModal} // click sul background chiude
    >
      <div
        className="relative w-screen h-screen"
        onClick={(e) => e.stopPropagation()} // non chiudere cliccando sul player
      >
        {/* Titolo centrato */}
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-[120] text-white text-base md:text-2xl font-bold text-center px-4">
          {title}
        </div>

        {/* Bottone chiudi */}
        <button
          onClick={closeModal}
          className="absolute top-4 right-4 z-[130] text-white text-3xl leading-none"
          aria-label="Chiudi"
        >
          ✕
        </button>

        {/* Area player */}
        <div className="absolute inset-0 z-[110] flex items-center justify-center">
          {/* Wrapper 16:9: togli se vuoi fill totale */}
          <div className="w-full h-full">
            {children}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}



// Attach child components
ModalProvider.Trigger = Trigger;
ModalProvider.Content = Content;

// Custom hook
const useModal = () => {
  const context = useContext(ModalContext);
  if (!context) throw new Error("useModal must be used within ModalProvider");
  return context;
};

export default useModal;
